import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  Avatar,
  IconButton,
} from "@mui/material";
import { Notifications, ArrowForward, Phone } from "@mui/icons-material";
import { format, parseISO, startOfDay, endOfDay } from "date-fns";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { COLORS } from "./constants";
import {
  setFrajunAutoInitiate,
  setFrajunContactName,
  setFrajunPhoneNumber,
  setVisible,
} from "services/Redux/userToken";

function avatarColor(name) {
  const LIST = ["#5c6bc0", "#26a69a", "#ef5350", "#ffa726", "#66bb6a", "#ab47bc"];
  if (!name) return LIST[0];
  let n = 0;
  for (let i = 0; i < name.length; i++) n += name.charCodeAt(i);
  return LIST[n % LIST.length];
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

function PriorityPill({ priority }) {
  if (!priority) return null;
  const lower = priority.toLowerCase();
  const map = {
    high: { color: "error", label: "High" },
    medium: { color: "warning", label: "Medium" },
    low: { color: "default", label: "Low" },
  };
  const cfg = map[lower] || { color: "default", label: priority };
  return (
    <Chip
      label={cfg.label}
      size="small"
      color={cfg.color}
      sx={{ borderRadius: "10px", fontWeight: 600, fontSize: "0.68rem", height: 20 }}
    />
  );
}

function DueText({ reminder, section }) {
  const dt = reminder.date ? parseISO(reminder.date) : null;
  if (!dt) return null;

  if (section === "overdue") {
    const diffMs = new Date() - dt;
    const diffDays = Math.floor(diffMs / 86400000);
    const text = diffDays === 0 ? "Overdue today" : `Overdue by ${diffDays} day${diffDays > 1 ? "s" : ""}`;
    return (
      <Typography variant="caption" sx={{ color: COLORS.red, fontWeight: 600 }}>
        {text}
      </Typography>
    );
  }
  if (section === "today") {
    const diffMs = dt - new Date();
    const diffHrs = Math.ceil(diffMs / 3600000);
    const text = diffHrs <= 1 ? "Due in < 1 hour" : `Due in ${diffHrs} hours`;
    return (
      <Typography variant="caption" sx={{ color: COLORS.indigo, fontWeight: 600 }}>
        {text}
      </Typography>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary">
      {format(dt, "MMM d, h:mm a")}
    </Typography>
  );
}

function getPrimaryPhone(contact) {
  const phones = contact?.phone_numbers || [];
  return phones.find((phone) => phone.primary)?.number || phones[0]?.number || "";
}

function ReminderCard({ reminder, section, contact, onCall }) {
  const contactName = reminder.contact_name || reminder.contact?.name || "Contact";
  const companyName = reminder.company_name || reminder.contact?.company || "";
  const title = reminder.notes || reminder.reminder_type?.name || "Reminder";
  const priority = reminder.priority;

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={1.5}
      py={1.25}
      sx={{ "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" } }}
    >
      <Avatar
        sx={{
          width: 34,
          height: 34,
          fontSize: "0.72rem",
          fontWeight: 700,
          bgcolor: avatarColor(contactName),
          flexShrink: 0,
        }}
      >
        {initials(contactName)}
      </Avatar>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          bgcolor:
            section === "overdue"
              ? "#fce4ec"
              : section === "today"
              ? "#fff3e0"
              : "#e8eaf6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Notifications
          sx={{
            fontSize: 16,
            color:
              section === "overdue"
                ? COLORS.red
                : section === "today"
                ? COLORS.amber
                : COLORS.indigo,
          }}
        />
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: "0.8rem" }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {companyName || contactName}
        </Typography>
        <Box mt={0.3}>
          <DueText reminder={reminder} section={section} />
        </Box>
      </Box>
      <Box flexShrink={0}>
        <PriorityPill priority={priority} />
        {(contact ? getPrimaryPhone(contact) : reminder.contact) && (
          <IconButton
            size="small"
            title="Make Call"
            aria-label="Make Call"
            onClick={() => onCall(reminder, contact)}
            sx={{ ml: 0.5 }}
          >
            <Phone sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export default function RemindersPanel({
  reminders = [],
  isLoading,
  activeTab,
  onTabChange,
  isActive = false,
  contactsMap = {},
}) {
  const [internalTab, setInternalTab] = useState(0);
  const isControlled = activeTab !== undefined && onTabChange !== undefined;
  const tab = isControlled ? activeTab : internalTab;
  const setTab = isControlled ? onTabChange : setInternalTab;
  const history = useHistory();
  const dispatch = useDispatch();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const now = new Date();

  const handleCall = async (reminder, knownContact) => {
    let contact = knownContact;
    // contactsMap only holds the first page of contacts, so fetch the rest on demand.
    if (!contact && reminder?.contact) {
      try {
        const res = await axios.get(`${domain}crm/contacts/${reminder.contact}/`, {
          headers: { Authorization: `token ${token}` },
        });
        contact = res.data;
      } catch {
        toast.error("Unable to fetch contact details");
        return;
      }
    }
    const phone = getPrimaryPhone(contact);
    if (!phone) {
      toast.error("No phone number found for this contact");
      return;
    }
    const name =
      contact.company ||
      [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
      "Contact";
    dispatch(setFrajunPhoneNumber(phone));
    dispatch(setFrajunContactName(name));
    dispatch(setFrajunAutoInitiate(false));
    dispatch(setVisible(true));
  };

  const { overdue, todayList, upcoming } = useMemo(() => {
    const pending = reminders.filter((r) => r.status !== "Completed");
    return {
      overdue: pending.filter((r) => r.date && new Date(r.date) < startOfDay(now)),
      todayList: pending.filter((r) => {
        if (!r.date) return false;
        const d = new Date(r.date);
        return d >= startOfDay(now) && d <= endOfDay(now);
      }),
      upcoming: pending.filter((r) => r.date && new Date(r.date) > endOfDay(now)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminders]);

  const lists = [overdue, todayList, upcoming];
  const current = lists[tab] ?? [];
  const sectionKeys = ["overdue", "today", "upcoming"];

  return (
    <Card sx={{
      borderRadius: "16px",
      boxShadow: isActive ? `0 0 0 2px ${COLORS.amber}, 0 6px 20px rgba(0,0,0,0.12)` : "0 2px 12px rgba(0,0,0,0.07)",
      transform: isActive ? "translateY(-4px)" : "none",
      transition: "all 0.3s ease",
      height: "100%",
    }}>
      <CardContent sx={{ p: 2.5, pb: "12px !important" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "0.95rem" }}>
            Reminders
          </Typography>
          <Typography
            variant="caption"
            component="span"
            onClick={() => history.push("/admin/crm/reminder")}
            sx={{ cursor: "pointer", color: COLORS.indigo, fontWeight: 600, display: "flex", alignItems: "center", gap: 0.3 }}
          >
            View all <ArrowForward sx={{ fontSize: 12 }} />
          </Typography>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            mb: 1.5,
            minHeight: 36,
            "& .MuiTab-root": { minHeight: 36, fontSize: "0.72rem", fontWeight: 700, py: 0.5 },
            "& .MuiTabs-indicator": { backgroundColor: COLORS.red },
            "& .Mui-selected": { color: `${COLORS.red} !important` },
          }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                Overdue
                {overdue.length > 0 && (
                  <Box
                    sx={{
                      bgcolor: COLORS.red,
                      color: "#fff",
                      borderRadius: "10px",
                      px: 0.8,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {overdue.length}
                  </Box>
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                Today
                {todayList.length > 0 && (
                  <Box
                    sx={{
                      bgcolor: COLORS.amber,
                      color: "#fff",
                      borderRadius: "10px",
                      px: 0.8,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {todayList.length}
                  </Box>
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                Upcoming
                {upcoming.length > 0 && (
                  <Box
                    sx={{
                      bgcolor: COLORS.indigo,
                      color: "#fff",
                      borderRadius: "10px",
                      px: 0.8,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      lineHeight: 1.6,
                    }}
                  >
                    {upcoming.length}
                  </Box>
                )}
              </Box>
            }
          />
        </Tabs>

        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Box key={i} display="flex" gap={1.5} mb={1.5}>
              <Skeleton variant="circular" width={34} height={34} />
              <Box flex={1}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="50%" />
              </Box>
            </Box>
          ))
        ) : current.length === 0 ? (
          <Box py={4} textAlign="center">
            <Notifications sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {tab === 0 ? "No overdue reminders" : tab === 1 ? "No reminders due today" : "No upcoming reminders"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
            {current.map((r) => (
              <ReminderCard
                key={r.id}
                reminder={r}
                section={sectionKeys[tab]}
                contact={contactsMap[r.contact] || contactsMap[String(r.contact)]}
                onCall={handleCall}
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
